import React, { useState, useEffect, useCallback } from 'react';
import QuoteListView from './QuoteListView';
import UserAdminView from './UserAdminView';
import AnalyticsView from './AnalyticsView';
import ApprovalReviewView from './ApprovalReviewView';
import ThresholdSettingsView from './ThresholdSettingsView';
import ProductsStepNested from './ProductsStepNested';
import { pricing } from './data/pricing';
import { useQuoteCalculations, useQuotePersistence, useNotifications } from './hooks';
import {
  steps,
  initialQuoteData,
  createEmptyQuote,
  canNavigateToStep as checkCanNavigate,
  getValidationError,
  hasProducts
} from './config';

// Import layout components
import { Header, Sidebar, StepNavigation, ToastContainer } from './components/layout';

// Import step components
import {
  DealInfoStep,
  PlatformStep,
  UsersStep,
  ServicesStep,
  InitialReviewStep,
  TermsStep,
  DiscountsStep,
  FinalReviewStep
} from './features/quote-wizard/steps';

/**
 * Main Quote Configurator Application
 * 
 * ⭐ H2: Enhanced step validation - blocks navigation to incomplete steps
 * ⭐ H4: Fixed New Quote bug - uses quoteKey to force complete re-render
 */
const QuoteConfiguratorApp = () => {
  // View & navigation state
  const [view, setView] = useState('list');
  const [currentStep, setCurrentStep] = useState(0);
  
  // ⭐ H4 FIX: Key to force remount of quote wizard on new quote
  const [quoteKey, setQuoteKey] = useState(Date.now());
  
  // User state
  const [user, setUser] = useState(null);

  // Notifications hook
  const notificationsData = useNotifications({ pollInterval: 30000 });

  // Quote identity state
  const [currentQuoteId, setCurrentQuoteId] = useState(null);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [quoteStatus, setQuoteStatus] = useState('draft');
  
  // Quote data state
  const [quoteData, setQuoteData] = useState(initialQuoteData);

  // Rejection feedback (shown when editing a rejected quote)
  const [rejectionFeedback, setRejectionFeedback] = useState(null);

  // Threshold breaches (shown when manager reviews a pending quote)
  const [thresholdBreaches, setThresholdBreaches] = useState(null);

  // Approval action state (for managers)
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState('');

  // ⭐ H2 FIX: Validation state for showing errors
  const [validationError, setValidationError] = useState(null);

  // Calculations hook
  const { 
    lineItems, 
    totals, 
    calculateLineItems, 
    calculatePEPM 
  } = useQuoteCalculations(quoteData);

  // Persistence hook
  const {
    saving,
    savingDraft,
    generatingPDF,
    showSuccess,
    showDraftSaved,
    handleSaveDraft,
    handleGenerateQuote,
    handleDownloadPDF
  } = useQuotePersistence({
    quoteData,
    calculateLineItems,
    totals,
    currentStep,
    currentQuoteId,
    setCurrentQuoteId,
    setQuoteNumber,
    setView
  });

  // Check for JWT token in URL fragment (from SAML redirect) and fetch user
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    // DEBUG: Log authentication state
    console.log('[Auth] Page load - URL hash:', window.location.hash ? 'present' : 'none');
    console.log('[Auth] Existing token in localStorage:', !!localStorage.getItem('authToken'));

    // Check if we have a token in the URL fragment (from SAML redirect)
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      const token = hash.split('token=')[1];
      console.log('[Auth] Found token in URL fragment, length:', token?.length);
      if (token) {
        localStorage.setItem('authToken', token);
        console.log('[Auth] Token saved to localStorage');
        // Clear the token from URL for security (don't leave it visible)
        window.history.replaceState(null, '', window.location.pathname);
      }
    }

    const fetchUser = async () => {
      const token = localStorage.getItem('authToken');
      console.log('[Auth] fetchUser called, has token:', !!token);

      // Build headers with token if available
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      try {
        const response = await fetch(`${API_URL}/auth/user`, {
          headers,
          credentials: 'include'
        });

        if (response.ok) {
          const userData = await response.json();
          // Create initials from display name
          const initials = userData.displayName
            ?.split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || userData.email[0].toUpperCase();

          setUser({
            id: userData.id,
            name: userData.displayName || userData.email.split('@')[0],
            email: userData.email,
            role: userData.role,
            avatar: initials
          });
        } else if (response.status === 401) {
          console.log('[Auth] Got 401 - token was:', !!localStorage.getItem('authToken'));
          // Token invalid or expired - clear it
          localStorage.removeItem('authToken');

          // Not authenticated - redirect to SAML login in production
          if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_API_URL) {
            console.log('[Auth] Redirecting to SAML login...');
            window.location.href = `${API_URL}/saml/login`;
            return;
          }
          // Fallback for local development
          setUser({
            name: 'Dev User',
            email: 'dev@splashbi.com',
            role: 'admin',
            avatar: 'DU'
          });
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        // Fallback for development
        setUser({
          name: 'Dev User',
          email: 'dev@splashbi.com',
          role: 'admin',
          avatar: 'DU'
        });
      }
    };

    fetchUser();
  }, []);

  // Clear validation error after timeout
  useEffect(() => {
    if (validationError) {
      const timer = setTimeout(() => setValidationError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [validationError]);

  // ⭐ H2: Navigation validation wrapper
  const canNavigateToStep = useCallback((targetStep) => 
    checkCanNavigate(targetStep, currentStep, quoteData), 
    [currentStep, quoteData]
  );

  // ⭐ H2: Get step completion status for visual indicators
  const getStepStatus = useCallback((stepIndex) => {
    // Step 0 (Deal Info) - needs customer name + deal type
    if (stepIndex === 0) {
      const hasCustomerName = quoteData.customerName?.trim();
      const hasDealType = !!quoteData.dealType;
      const needsEmployees = (quoteData.dealType === 'HCM' || quoteData.dealType === 'Cross-Domain');
      const hasEmployees = !needsEmployees || quoteData.employeeCount > 0;
      return hasCustomerName && hasDealType && hasEmployees;
    }
    
    // Step 1 (Platform) - needs platform + deployment
    if (stepIndex === 1) {
      return (quoteData.hasSplashBI || quoteData.hasSplashAI) && quoteData.deployment;
    }
    
    // Step 2 (Users) - optional, always "complete"
    if (stepIndex === 2) {
      return true;
    }
    
    // Step 3 (Products) - needs at least one product
    if (stepIndex === 3) {
      return hasProducts(quoteData);
    }
    
    // Steps 4+ are optional configuration
    return true;
  }, [quoteData]);

  // ⭐ H2: Step navigation with proper validation
  const handleStepNavigation = useCallback((targetStep) => {
    // Always allow going back
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      setValidationError(null);
      return;
    }
    
    // Check if we can navigate forward
    if (canNavigateToStep(targetStep)) {
      setCurrentStep(targetStep);
      setValidationError(null);
    } else {
      const error = getValidationError(targetStep, quoteData);
      setValidationError(error);
    }
  }, [currentStep, canNavigateToStep, quoteData]);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    setCurrentStep(Math.max(0, currentStep - 1));
    setValidationError(null);
  }, [currentStep]);
  
  // ⭐ H2: Enhanced Next handler with proper validation
  const handleNext = useCallback(() => {
    const nextStep = currentStep + 1;
    
    // Check validation for moving to next step
    if (!canNavigateToStep(nextStep)) {
      const error = getValidationError(nextStep, quoteData);
      setValidationError(error);
      return;
    }
    
    setCurrentStep(Math.min(steps.length - 1, nextStep));
    setValidationError(null);
  }, [currentStep, canNavigateToStep, quoteData]);

  // ⭐ H2: Check if current step allows proceeding
  const canProceedToNextStep = useCallback(() => {
    if (currentStep >= steps.length - 1) return true; // Last step
    return canNavigateToStep(currentStep + 1);
  }, [currentStep, canNavigateToStep]);

  // Quote management handlers
  const handleLoadQuote = useCallback((quote, isNew = false) => {
    if (isNew) {
      // ⭐ H4 FIX: Generate new key to force complete remount
      const newKey = Date.now();
      setQuoteKey(newKey);
      setQuoteData(createEmptyQuote());
      setCurrentQuoteId(null);
      setQuoteNumber('');
      setQuoteStatus('draft');
      setCurrentStep(0);
      setValidationError(null);
      setRejectionFeedback(null);
      setThresholdBreaches(null);
    } else {
      setQuoteData(quote.quote_data);
      setCurrentQuoteId(quote.id);
      setQuoteNumber(quote.quote_number);
      setQuoteStatus(quote.status || 'draft');
      setCurrentStep(quote.current_step ?? 8);
      setQuoteKey(Date.now()); // Also remount when loading existing quote
      // Show rejection feedback if quote was previously rejected
      setRejectionFeedback(quote.rejection_feedback || null);
      // Show threshold breaches for pending approval quotes
      const breaches = quote.threshold_breaches;
      setThresholdBreaches(
        breaches ? (typeof breaches === 'string' ? JSON.parse(breaches) : breaches) : null
      );
    }
    setView('create');
  }, []);

  // Handler to view a quote by ID (used by Analytics pipeline aging)
  const handleViewQuoteById = useCallback(async (quoteId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/api/quotes/${quoteId}`, {
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const quote = await response.json();
        handleLoadQuote(quote);
      } else {
        console.error('Failed to fetch quote:', response.status);
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
    }
  }, [handleLoadQuote]);

  // ⭐ H4 FIX: Completely reset state for new quote
  const handleNewQuote = useCallback(() => {
    // Generate new key to force React to unmount and remount all child components
    const newKey = Date.now();

    // Reset all state
    setQuoteKey(newKey);
    setQuoteData(createEmptyQuote());
    setCurrentQuoteId(null);
    setQuoteNumber('');
    setQuoteStatus('draft');
    setCurrentStep(0);
    setValidationError(null);
    setRejectionFeedback(null);
    setThresholdBreaches(null);
    setView('create');

    // Log for debugging
    console.log('New Quote created with key:', newKey);
  }, []);

  // Handle notification click - navigate to relevant quote
  const handleNotificationClick = useCallback((notification) => {
    if (notification.quote_id) {
      // For approval notifications, go to review view
      if (notification.type === 'approval_required') {
        setView('review');
      } else {
        // For approved/rejected, go to quote list
        setView('list');
      }
    }
    // Refresh notifications
    notificationsData.refresh();
  }, [notificationsData]);

  // Handle quote approval (manager action)
  const handleApproveQuote = useCallback(async () => {
    if (!currentQuoteId) return;

    setApprovalLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/quotes/${currentQuoteId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include'
      });

      if (response.ok) {
        setQuoteStatus('approved');
        setThresholdBreaches(null);
        notificationsData.refresh();
        alert('Quote approved successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to approve quote');
      }
    } catch (error) {
      console.error('Error approving quote:', error);
      alert('Failed to approve quote');
    } finally {
      setApprovalLoading(false);
    }
  }, [currentQuoteId, notificationsData]);

  // Handle quote rejection (manager action)
  const handleRejectQuote = useCallback(async () => {
    if (!currentQuoteId || !rejectFeedback.trim()) {
      alert('Please provide feedback for the rejection');
      return;
    }

    setApprovalLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/quotes/${currentQuoteId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include',
        body: JSON.stringify({ feedback: rejectFeedback })
      });

      if (response.ok) {
        setShowRejectModal(false);
        setRejectFeedback('');
        setThresholdBreaches(null);
        notificationsData.refresh();
        alert('Quote rejected and returned to draft');
        setView('review'); // Go back to review tab
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to reject quote');
      }
    } catch (error) {
      console.error('Error rejecting quote:', error);
      alert('Failed to reject quote');
    } finally {
      setApprovalLoading(false);
    }
  }, [currentQuoteId, rejectFeedback, notificationsData]);

  // Render step content
  // ⭐ H4 FIX: All step components receive the quoteKey as key to force remount
  const renderStepContent = () => {
    const commonProps = { quoteData, setQuoteData };
    
    const stepComponents = [
      <DealInfoStep key={`deal-${quoteKey}`} {...commonProps} user={user} />,
      <PlatformStep key={`platform-${quoteKey}`} {...commonProps} pricing={pricing} />,
      <UsersStep key={`users-${quoteKey}`} {...commonProps} pricing={pricing} />,
      <ProductsStepNested key={`products-${quoteKey}`} {...commonProps} pricing={pricing} />,
      <ServicesStep key={`services-${quoteKey}`} {...commonProps} pricing={pricing} />,
      <InitialReviewStep key={`review1-${quoteKey}`} items={lineItems} totals={totals} calculatePEPM={calculatePEPM} />,
      <DiscountsStep key={`discounts-${quoteKey}`} {...commonProps} items={lineItems} calculatePEPM={calculatePEPM} />,
      <TermsStep key={`terms-${quoteKey}`} {...commonProps} />,
      <FinalReviewStep key={`final-${quoteKey}`} quoteData={quoteData} items={lineItems} totals={totals} calculatePEPM={calculatePEPM} />
    ];
    return stepComponents[currentStep] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Toast Notifications */}
      <ToastContainer
        showSuccess={showSuccess}
        showDraftSaved={showDraftSaved}
        quoteNumber={quoteNumber}
        currentStepName={steps[currentStep].name}
        totalARR={totals.total}
      />

      {/* Header */}
      <Header
        view={view}
        onViewList={() => setView('list')}
        onNewQuote={handleNewQuote}
        onViewAdmin={() => setView('admin')}
        onViewAnalytics={() => setView('analytics')}
        onViewReview={() => setView('review')}
        onViewThresholds={() => setView('thresholds')}
        user={user}
        notifications={notificationsData}
        onNotificationClick={handleNotificationClick}
      />

      {/* Main Content */}
      {view === 'list' ? (
        <QuoteListView onLoadQuote={handleLoadQuote} user={user} />
      ) : view === 'admin' ? (
        <UserAdminView />
      ) : view === 'analytics' ? (
        <AnalyticsView onViewQuote={handleViewQuoteById} />
      ) : view === 'review' ? (
        <ApprovalReviewView onViewQuote={handleLoadQuote} />
      ) : view === 'thresholds' ? (
        <ThresholdSettingsView />
      ) : (
        <div key={quoteKey} className="max-w-7xl mx-auto px-6 py-8">
          {/* Manager Approval Banner - shown when manager views a pending_approval quote */}
          {quoteStatus === 'pending_approval' && ['super_admin', 'admin', 'ceo', 'cro', 'cto', 'manager'].includes(user?.role?.toLowerCase()) && (
            <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">!</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800 mb-2">Quote Pending Your Approval</h4>
                  {thresholdBreaches && thresholdBreaches.length > 0 && (
                    <div className="mb-3">
                      <p className="text-amber-700 text-sm mb-2">Discount thresholds exceeded:</p>
                      <div className="flex flex-wrap gap-2">
                        {thresholdBreaches.map((breach, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                            {breach.category}: {breach.discountApplied}% (max {breach.maxAllowed}%)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 mt-3">
                    <button
                      onClick={handleApproveQuote}
                      disabled={approvalLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {approvalLoading ? 'Processing...' : 'Approve Quote'}
                    </button>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={approvalLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      Reject Quote
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejection Feedback Banner - shown when editing a previously rejected quote */}
          {rejectionFeedback && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">!</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-red-800 mb-1">Quote Previously Rejected</h4>
                  <p className="text-red-700 text-sm mb-2">Manager feedback:</p>
                  <p className="text-red-800 bg-red-100 p-3 rounded-lg text-sm italic">"{rejectionFeedback}"</p>
                  <p className="text-red-600 text-xs mt-2">Please address the feedback and resubmit.</p>
                </div>
                <button
                  onClick={() => setRejectionFeedback(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  &times;
                </button>
              </div>
            </div>
          )}

          {/* Reject Modal */}
          {showRejectModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 bg-red-50">
                  <h3 className="text-lg font-semibold text-red-800">Reject Quote</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-4">
                    Please provide feedback explaining why this quote is being rejected.
                    The sales rep will see this feedback and can revise the quote.
                  </p>
                  <textarea
                    value={rejectFeedback}
                    onChange={(e) => setRejectFeedback(e.target.value)}
                    placeholder="e.g., Please reduce platform discount to 18% or provide business justification..."
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={4}
                  />
                </div>
                <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectFeedback('');
                    }}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRejectQuote}
                    disabled={!rejectFeedback.trim() || approvalLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {approvalLoading ? 'Rejecting...' : 'Reject Quote'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ⭐ H2: Validation Error Banner */}
          {validationError && (
            <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl animate-pulse">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">!</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800 mb-1">Cannot Proceed</h4>
                  <p className="text-amber-700 text-sm whitespace-pre-line">{validationError}</p>
                </div>
                <button 
                  onClick={() => setValidationError(null)}
                  className="text-amber-600 hover:text-amber-800"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-12 gap-8">
            {/* Left Sidebar */}
            <div className="col-span-12 lg:col-span-3">
              <Sidebar
                steps={steps}
                currentStep={currentStep}
                onStepClick={handleStepNavigation}
                canNavigateToStep={canNavigateToStep}
                getStepStatus={getStepStatus}
                totals={totals}
              />
            </div>

            {/* Main Content Area */}
            <div className="col-span-12 lg:col-span-9">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                {renderStepContent()}
              </div>

              {/* Navigation */}
              <StepNavigation
                currentStep={currentStep}
                steps={steps}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onSaveDraft={handleSaveDraft}
                onGenerateQuote={handleGenerateQuote}
                onDownloadPDF={handleDownloadPDF}
                canSaveDraft={currentStep > 0 && quoteData.customerName?.trim()}
                canProceed={canProceedToNextStep()}
                saving={saving}
                savingDraft={savingDraft}
                generatingPDF={generatingPDF}
                quoteNumber={quoteNumber}
                quoteId={currentQuoteId}
                quoteStatus={quoteStatus}
                onStatusChange={(status) => setQuoteStatus(status)}
                validationError={validationError}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteConfiguratorApp;
